import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft } from "lucide-react";

export default function ResetPasswordPage() {
  const { t, lang } = useI18n();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError(lang === "it" ? "Le password non coincidono" : "Passwords do not match");
      return;
    }
    if (!password) {
      setError(lang === "it" ? "Password obbligatoria" : "Password required");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success(lang === "it" ? "Password aggiornata" : "Password updated");
      nav("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="mb-10">
          <img
            src="https://customer-assets-39nsmqrw.emergentagent.net/job_attestguard/artifacts/3wuvsw34_FORMATO-WEB-NERO.jpg"
            alt="Sicurgarda"
            className="h-10 w-auto"
          />
        </div>

        <h2 className="text-3xl font-bold tracking-tight">{t("reset_password_title")}</h2>
        <p className="text-muted-foreground text-sm mt-2">
          {token
            ? (lang === "it" ? "Inserisci la nuova password." : "Enter your new password.")
            : (lang === "it" ? "Token mancante nell'URL." : "Missing token in URL.")}
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("new_password")}</Label>
            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" className="mt-2 h-12" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("confirm_password")}</Label>
            <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" className="mt-2 h-12" />
          </div>
          {error && <div className="text-sm text-[hsl(0,70%,40%)] bg-[hsl(0,90%,94%)] border rounded-md px-3 py-2">{error}</div>}
          <Button type="submit" disabled={loading || !token} data-testid="reset-submit-button" className="w-full h-12 rounded-full btn-lift">
            {loading ? "…" : (lang === "it" ? "Aggiorna password" : "Update password")}
          </Button>
        </form>

        <Link to="/login" className="inline-flex items-center gap-1.5 mt-8 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground btn-lift">
          <ArrowLeft className="w-3.5 h-3.5" /> {t("back_to_login")}
        </Link>
      </div>
    </div>
  );
}
